'use client'

import { Button } from '@/core/components/form/button/button'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useRef } from 'react'
import { LuArrowLeft } from 'react-icons/lu'

import { cn } from '@/core/utils/common'

import AccountIcons from '@/features/veluvit/components/shell/account-icons'
import BarGlass from '@/features/veluvit/components/shell/bar-glass'
import Logo from '@/features/veluvit/components/shell/logo'
import SafeToggle from '@/features/veluvit/components/shell/safe-toggle'
import { activeTab, NAV } from '@/features/veluvit/components/shell/nav-items'
import { ICON_BTN } from '@/features/veluvit/components/ui/pill-class'
import { back } from '@/features/veluvit/lib/navigation'
import { useUiStore } from '@/features/veluvit/stores/ui.store'

import type { ReactNode } from 'react'

type PageHeaderProps = {
  back?: boolean
  title?: string
  solid?: boolean
  /** 머리줄 아래 같은 유리 판 안에 붙는 줄(TabRow 등) — 따로 sticky 로 두면 블러 두 장이 각자 다른 뒤를 비춰 색이 갈렸다 */
  children?: ReactNode
}

/**
 * 앱 머리줄 — 모바일 · PC 의 책임이 다르다(sai-project/layout-policy.md 규칙 4).
 * - 모바일: [로고(또는 제목)] 또는 [← 뒤로 + 제목] · 오른쪽 알림 · 마이. 메뉴(☰)는 하단 탭 맨 왼쪽(bottom-tabs → nav-drawer).
 * - PC: 로고 · 메뉴 · 재화 · 계정은 상단 내비(gnb)가 맡는다. 머리줄(로고 · 제목 · 뒤로)은 접고 **아래 줄(children, 탭 줄 등)만** 내비 바로 밑에 붙인다(2026-10-06 — PC 는 뒤로 · 제목 줄이 필요 없다).
 *   그 줄은 자기 유리를 깔지 않는다 — 높이를 ui.store barHead 로 알려 상단 내비 유리가 그 줄까지 내려와 한 장이 된다.
 * children(TabRow 등)은 같은 유리 판 아래 줄로 붙는다. 재화는 머리줄에 두지 않는다 — 마이 · 충전 창 · 채팅방에서 보인다.
 */
export default function PageHeader({
  back: withBack,
  title,
  solid,
  children,
}: PageHeaderProps) {
  const pathname = usePathname()
  const ref = useRef<HTMLElement>(null)

  // PC — 이 머리줄(남는 건 아래 줄뿐)의 높이를 상단 내비에 알린다. 화면을 떠나면 0 으로
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const desk = window.matchMedia('(min-width: 1001px)')
    const sync = () =>
      useUiStore.getState().set({ barHead: desk.matches ? el.offsetHeight : 0 })
    const observer = new ResizeObserver(sync)
    observer.observe(el)
    desk.addEventListener('change', sync)
    return () => {
      observer.disconnect()
      desk.removeEventListener('change', sync)
      useUiStore.getState().set({ barHead: 0 })
    }
  }, [])

  return (
    <>
      <header
        ref={ref}
        data-pagebar
        className={cn(
          // 글래스 머리줄 — 위로 밀려 올라가는 내용(배너 · 카드)이 흐리게 비친다
          // 안전 영역(#view 의 위 여백)까지 끌어올려 덮고, 그만큼 안쪽 여백을 둔다. 유리는 이 판 한 장 — 아래 줄(children)까지 같은 블러 · 같은 색
          // 유리 바탕은 맨 뒤 BarGlass 가 깐다(홈 칩 줄이 붙으면 그 밑까지 늘어난다)
          'sticky top-[calc(var(--safe-top)*-1)] z-30 mt-[calc(var(--safe-top)*-1)] pt-(--safe-top)',
          // PC — #view 의 위 여백(58px, 상단 내비 자리)이 sticky 기준선을 이미 내려 주므로 top 0 이 곧 내비 바로 밑이다
          // 상단 내비(z-50)의 늘어난 유리 위에 글자가 올라와야 해서 PC 에선 그보다 위에 쌓는다
          'over10:top-0 over10:z-[55] over10:mt-0 over10:pt-0',
          solid && 'border-b border-line'
        )}
      >
        <BarGlass on="mobile" />
        <div className="flex h-(--header-h) items-center gap-1.5 px-4 over10:hidden">
          {withBack && (
            <Button
              type="button"
              aria-label="뒤로"
              onClick={back}
              className={cn(ICON_BTN, '-ml-2')}
              variant="bare"
            >
              <LuArrowLeft aria-hidden="true" className="size-5.5" />
            </Button>
          )}
          {title ? (
            <span
              className={cn(
                'min-w-0 truncate text-c19 font-bold over10:text-c24',
                !withBack && 'pl-1'
              )}
            >
              {title}
            </span>
          ) : (
            // 로고 + 홈 · 랭킹 글자 탭(홈 시안 A) — 고른 쪽만 흰 글자, 나머지는 옅게
            <>
              <Link href="/" aria-label="홈" className="flex-none no-underline">
                <Logo className="text-c20" />
              </Link>
              <nav
                aria-label="홈 메뉴"
                className="ml-3 hidden gap-3.5 text-c16 font-bold over4:flex"
              >
                {[NAV.char /* , NAV.ranking — 임시 숨김 */].map((item) => {
                  const on = item.k === activeTab(pathname)
                  return (
                    <Link
                      key={item.k}
                      href={item.href}
                      aria-current={on ? 'page' : undefined}
                      className={cn(
                        'no-underline transition-colors',
                        on ? 'text-t1' : 'text-t4 hover:text-t2'
                      )}
                    >
                      {item.label}
                    </Link>
                  )
                })}
              </nav>
            </>
          )}
          <span className="flex-1" />
          {/* 오른쪽 — 알림 · 마이(로그인 여부와 상관없이 같은 사람 아이콘). PC 는 상단 내비에 같은 자리가 있어 모바일에서만 */}
          {/* 오른쪽 영역 맨 왼쪽 — 세이프 토글, 이어서 알림 · 마이 */}
          <SafeToggle className="mr-1 over10:hidden" />
          <AccountIcons className="-mr-2 over10:hidden" />
        </div>
        {children}
      </header>
    </>
  )
}
